import { mulberry32 } from '../core/rng.js';

// ---------------------------------------------------------------------------
// Everything that makes the polder feel inhabited: the green weatherboard
// houses of the Zaan, pollarded willows, poplar windbreaks, reed beds along
// the water, fenced meadows and the sheep in them.
// ---------------------------------------------------------------------------

export function registerPropBlocks(palette) {
    return {
        // Zaanse houses are famously dark green board with white trim.
        wallGreen: palette.add('wallGreen', 0x476c53, { jitter: 0.06 }),
        wallGreenDark: palette.add('wallGreenDark', 0x37573f, { jitter: 0.06 }),
        wallBlue: palette.add('wallBlue', 0x42637d, { jitter: 0.06 }),
        trimWhite: palette.add('trimWhite', 0xe9e6da, { jitter: 0.035 }),
        roofRed: palette.add('roofRed', 0xa85a45, { jitter: 0.07 }),
        roofSlate: palette.add('roofSlate', 0x5f6570, { jitter: 0.07 }),
        thatch: palette.add('thatch', 0xa88c5c, { jitter: 0.09 }),
        timber: palette.add('timber', 0x8a6f46, { jitter: 0.07 }),
        timberDark: palette.add('timberDark', 0x634e30, { jitter: 0.06 }),
        // Emissive: dark at noon, warm at midnight.
        window: palette.add('window', 0xffcf8a, { emissive: 1.0, jitter: 0.02 }),
        windowCold: palette.add('windowCold', 0x33404e, { emissive: 0.35, jitter: 0.03 }),
        chimney: palette.add('chimney', 0xa06456, { jitter: 0.07 }),
        leafPoplar: palette.add('leafPoplar', 0x5d8c43, { jitter: 0.1 }),
        leafWillow: palette.add('leafWillow', 0x82a457, { jitter: 0.1 }),
        bark: palette.add('bark', 0x6f5c42, { jitter: 0.07 }),
        reed: palette.add('reed', 0x93a05a, { jitter: 0.12 }),
        wool: palette.add('wool', 0xe6e2d6, { jitter: 0.05 }),
        sheepFace: palette.add('sheepFace', 0x2b2b2e, { jitter: 0.04 }),
        tulipRed: palette.add('tulipRed', 0xc2402f, { jitter: 0.06 }),
        tulipYellow: palette.add('tulipYellow', 0xdcb03a, { jitter: 0.06 }),
        tulipPink: palette.add('tulipPink', 0xcc6b93, { jitter: 0.06 }),
        lamp: palette.add('lamp', 0xffd08a, { emissive: 1.0, jitter: 0.02 }),
        lampPost: palette.add('lampPost', 0x2f2c28, { jitter: 0.05 }),
    };
}

/**
 * A canal lantern on a post. Returns the grid position of the flame so the
 * caller can register a matching point light.
 */
export function stampLantern(grid, P, polder, x, z) {
    const base = polder.top(x, z);
    if (base < polder.seaLevel) return null;
    const h = 4;
    for (let k = 1; k <= h; k++) grid.set(x, base + k, z, P.lampPost);
    grid.set(x, base + h + 1, z, P.lamp);
    grid.set(x, base + h + 2, z, P.lampPost);   // little cap over the flame
    return { x, y: base + h + 1, z };
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

/**
 * A Zaanse house: solid green board box, white trim course, gabled roof with
 * an overhang, lit windows and a brick chimney.
 *
 * Houses are stamped straight into the world grid so they share ambient
 * occlusion with the ground they stand on.
 */
export function stampHouse(grid, P, polder, cx, cz, opts = {}) {
    const {
        w = 7,
        d = 5,
        wallHeight = 4,
        wall = P.wallGreen,
        roof = P.roofRed,
        rng = Math.random,
    } = opts;

    const x0 = cx - (w >> 1);
    const z0 = cz - (d >> 1);
    const x1 = x0 + w - 1;
    const z1 = z0 + d - 1;

    // Sit on the highest ground under the footprint so nothing floats.
    let base = -Infinity;
    for (let z = z0; z <= z1; z++) {
        for (let x = x0; x <= x1; x++) {
            const t = polder.top(x, z);
            if (t < 0) return false;
            if (t > base) base = t;
        }
    }
    if (base < polder.seaLevel) return false;
    const y0 = base + 1;

    // Foundation course, so a house on uneven ground still meets the grass.
    grid.box(x0, base - 1, z0, x1, base, z1, P.timberDark);

    // Walls (solid — the inside is never seen, and solid meshes cheaper).
    grid.box(x0, y0, z0, x1, y0 + wallHeight - 1, z1, wall);

    // White trim course under the eaves.
    grid.box(x0, y0 + wallHeight - 1, z0, x1, y0 + wallHeight - 1, z1, P.trimWhite);

    // Gabled roof, ridge along the long axis, with a one-voxel overhang.
    const alongX = w >= d;
    const span = alongX ? d : w;
    const steps = Math.ceil(span / 2);
    for (let k = 0; k < steps; k++) {
        const y = y0 + wallHeight + k;
        const ax0 = alongX ? x0 - 1 : x0 + k;
        const ax1 = alongX ? x1 + 1 : x1 - k;
        const az0 = alongX ? z0 + k : z0 - 1;
        const az1 = alongX ? z1 - k : z1 + 1;
        if (ax0 > ax1 || az0 > az1) break;
        grid.box(ax0, y, az0, ax1, y, az1, k === 0 ? P.trimWhite : roof);
    }

    // Windows on all four faces, in the top half of the wall.
    const wy = y0 + wallHeight - 3;
    const lit = P.window;
    for (let x = x0 + 1; x <= x1 - 1; x += 2) {
        grid.set(x, wy, z0, lit);
        grid.set(x, wy + 1, z0, P.trimWhite);
        grid.set(x, wy, z1, lit);
        grid.set(x, wy + 1, z1, P.trimWhite);
    }
    for (let z = z0 + 1; z <= z1 - 1; z += 2) {
        grid.set(x0, wy, z, lit);
        grid.set(x1, wy, z, lit);
    }

    // Door with a white frame on the long front face.
    const dx = cx;
    grid.set(dx, y0, z1, P.timberDark);
    grid.set(dx, y0 + 1, z1, P.timberDark);
    grid.set(dx - 1, y0, z1, P.trimWhite);
    grid.set(dx - 1, y0 + 1, z1, P.trimWhite);
    grid.set(dx + 1, y0, z1, P.trimWhite);
    grid.set(dx + 1, y0 + 1, z1, P.trimWhite);

    // Chimney.
    const chx = x0 + 1 + Math.floor(rng() * Math.max(1, w - 2));
    const chTop = y0 + wallHeight + steps + 1;
    grid.box(chx, y0 + wallHeight, cz, chx, chTop, cz, P.chimney);
    grid.set(chx, chTop + 1, cz, P.trimWhite);

    return true;
}

/** Lombardy poplar — the tall thin sentries that line every Dutch road. */
export function stampPoplar(grid, P, polder, x, z, rng) {
    const base = polder.top(x, z);
    if (base < polder.seaLevel) return false;
    const h = 9 + Math.floor(rng() * 5);
    grid.box(x, base + 1, z, x, base + h, z, P.bark);
    for (let k = 2; k < h + 3; k++) {
        const y = base + k;
        const t = (k - 2) / (h + 1);
        // Narrow flame silhouette: fat in the middle, pointed at both ends.
        const r = 2.1 * Math.sin(Math.PI * Math.pow(t, 0.75)) + 0.35;
        const ri = Math.round(r);
        for (let dz = -ri; dz <= ri; dz++) {
            for (let dx = -ri; dx <= ri; dx++) {
                if (dx * dx + dz * dz > r * r) continue;
                if (rng() < 0.09) continue;             // ragged edges
                grid.setIfEmpty(x + dx, y, z + dz, P.leafPoplar);
            }
        }
    }
    return true;
}

/** Pollarded willow — stumpy trunk, shaggy ball. Grows in every wet ditch. */
export function stampWillow(grid, P, polder, x, z, rng) {
    const base = polder.top(x, z);
    if (base < polder.seaLevel) return false;
    const trunk = 3 + Math.floor(rng() * 2);
    grid.box(x, base + 1, z, x, base + trunk, z, P.bark);
    grid.set(x + 1, base + trunk, z, P.bark);
    const cy = base + trunk + 2;
    for (let dy = -2; dy <= 2; dy++) {
        const r = 2.6 - Math.abs(dy) * 0.55;
        const ri = Math.round(r);
        for (let dz = -ri; dz <= ri; dz++) {
            for (let dx = -ri; dx <= ri; dx++) {
                if (dx * dx + dz * dz > r * r) continue;
                if (rng() < 0.14) continue;
                grid.setIfEmpty(x + dx, cy + dy, z + dz, P.leafWillow);
            }
        }
    }
    return true;
}

/** A sheep. Four legs, a body, and a face that is mostly opinion. */
export function stampSheep(grid, P, polder, x, z, facing, rng) {
    const base = polder.top(x, z);
    if (base < polder.seaLevel) return false;
    const y = base + 1;
    const fx = facing === 0 ? 1 : 0;
    const fz = facing === 0 ? 0 : 1;

    // Legs
    for (const [lx, lz] of [[0, 0], [fx * 2, fz * 2], [fz, fx], [fx * 2 + fz, fz * 2 + fx]]) {
        grid.setIfEmpty(x + lx, y, z + lz, P.sheepFace);
    }
    // Woolly body
    grid.box(x, y + 1, z, x + fx * 2 + fz, y + 2, z + fz * 2 + fx, P.wool);
    // Head, one step forward and slightly down (grazing)
    grid.setIfEmpty(x + fx * 3, y + 1, z + fz * 3, P.sheepFace);
    return true;
}

/** Post-and-rail fence between two cells. */
export function stampFence(grid, P, polder, x0, z0, x1, z1) {
    const steps = Math.max(Math.abs(x1 - x0), Math.abs(z1 - z0));
    for (let i = 0; i <= steps; i++) {
        const x = Math.round(x0 + ((x1 - x0) * i) / steps);
        const z = Math.round(z0 + ((z1 - z0) * i) / steps);
        const base = polder.top(x, z);
        if (base < polder.seaLevel) continue;
        grid.setIfEmpty(x, base + 1, z, P.timber);       // rail height
        if (i % 3 === 0) grid.setIfEmpty(x, base + 2, z, P.timber); // post
    }
}

// ---------------------------------------------------------------------------
// Scattering
// ---------------------------------------------------------------------------

/** Reed beds wherever dry land meets water. */
export function scatterReeds(grid, P, polder, seed) {
    const rng = mulberry32(seed);
    const { size, seaLevel } = polder;
    for (let z = 1; z < size - 1; z++) {
        for (let x = 1; x < size - 1; x++) {
            const top = polder.top(x, z);
            if (top !== seaLevel && top !== seaLevel + 1) continue;
            // Only right at the edge: at least one neighbour must be underwater.
            const wet =
                polder.top(x + 1, z) < seaLevel ||
                polder.top(x - 1, z) < seaLevel ||
                polder.top(x, z + 1) < seaLevel ||
                polder.top(x, z - 1) < seaLevel;
            if (!wet || rng() > 0.42) continue;
            const h = 1 + Math.floor(rng() * 3);
            for (let k = 1; k <= h; k++) grid.setIfEmpty(x, top + k, z, P.reed);
        }
    }
}

/** Tulip strips — a nod to the bulb fields, planted in tidy Dutch rows. */
export function scatterTulips(grid, P, polder, seed) {
    const rng = mulberry32(seed);
    const { size } = polder;
    const colors = [P.tulipRed, P.tulipYellow, P.tulipPink];
    const fields = 3;
    for (let f = 0; f < fields; f++) {
        const cx = 18 + Math.floor(rng() * (size - 36));
        const cz = 18 + Math.floor(rng() * (size - 36));
        if (polder.distToCanal(cx, cz) < 12) continue;
        const w = 7 + Math.floor(rng() * 8);
        const d = 9 + Math.floor(rng() * 10);
        for (let z = cz; z < cz + d; z++) {
            const color = colors[(z - cz) % colors.length];
            for (let x = cx; x < cx + w; x++) {
                if (!polder.isDryLand(x, z)) continue;
                if (polder.distToCanal(x, z) < 10) continue;
                if (rng() < 0.12) continue;
                const top = polder.top(x, z);
                grid.setIfEmpty(x, top + 1, z, color);
            }
        }
    }
}

/**
 * Lay out the village: a row of houses following the right bank of the Zaan,
 * fenced meadows with sheep on the left, poplar windbreaks along the paths,
 * willows in the ditches.
 */
export function buildVillage(grid, P, polder, seed, exclusions = []) {
    const rng = mulberry32(seed);
    const { size } = polder;

    // Keep the village out of the mills' yards.
    const blocked = (x, z) =>
        exclusions.some((e) => (x - e.x) ** 2 + (z - e.z) ** 2 < e.r * e.r);

    // Positions worth hanging a light on, handed back to the caller.
    const lights = [];

    // --- houses along the far bank ---------------------------------------
    const roofs = [P.roofRed, P.roofSlate, P.roofRed];
    const walls = [P.wallGreen, P.wallGreenDark, P.wallGreen, P.wallBlue];
    let placed = 0;
    for (let i = 0; i < 14 && placed < 9; i++) {
        const t = 0.30 + (i / 13) * 0.42;
        const spot = polder.bankSpot(t, 11 + Math.floor(rng() * 5));
        if (!polder.isDryLand(spot.x, spot.z) || blocked(spot.x, spot.z)) continue;
        const ok = stampHouse(grid, P, polder, spot.x, spot.z, {
            w: 6 + Math.floor(rng() * 4),
            d: 5 + Math.floor(rng() * 2),
            wallHeight: 4 + (rng() < 0.3 ? 1 : 0),
            wall: walls[i % walls.length],
            roof: roofs[i % roofs.length],
            rng,
        });
        if (ok) {
            placed++;
            // A glow just outside the front door, so the house lights the
            // ground it stands on instead of only glowing at the windows.
            lights.push({ x: spot.x, y: polder.top(spot.x, spot.z) + 4, z: spot.z, range: 11 });
        }
    }

    // A couple of barns set back from the water.
    for (let i = 0; i < 3; i++) {
        const t = 0.36 + i * 0.14;
        const spot = polder.bankSpot(t, 24 + Math.floor(rng() * 6));
        if (blocked(spot.x, spot.z)) continue;
        stampHouse(grid, P, polder, spot.x, spot.z, {
            w: 9,
            d: 7,
            wallHeight: 4,
            wall: P.timberDark,
            roof: P.thatch,
            rng,
        });
    }

    // --- the towpath along the mill bank ----------------------------------
    for (let i = 0; i <= 200; i++) {
        const t = i / 200;
        const spot = polder.bankSpot(t, -9);
        for (let w = -1; w <= 1; w++) {
            const x = spot.x + w;
            if (!polder.isDryLand(x, spot.z)) continue;
            polder.paint(x, spot.z, polder.blocks.path);
        }
    }

    // --- lanterns down the towpath ----------------------------------------
    for (let i = 0; i < 9; i++) {
        const t = 0.10 + (i / 8) * 0.80;
        const spot = polder.bankSpot(t, -7);
        if (blocked(spot.x, spot.z)) continue;
        const lamp = stampLantern(grid, P, polder, spot.x, spot.z);
        if (lamp) lights.push({ ...lamp, range: 13 });
    }

    // --- poplars lining the towpath ---------------------------------------
    for (let i = 0; i < 34; i++) {
        const t = 0.05 + (i / 34) * 0.9;
        const spot = polder.bankSpot(t, -13 - Math.floor(rng() * 2));
        if (rng() < 0.25 || blocked(spot.x, spot.z)) continue;
        stampPoplar(grid, P, polder, spot.x, spot.z, rng);
    }

    // --- willows scattered through the wet meadows ------------------------
    for (let i = 0; i < 26; i++) {
        const x = 8 + Math.floor(rng() * (size - 16));
        const z = 8 + Math.floor(rng() * (size - 16));
        if (polder.distToCanal(x, z) < 14) continue;
        if (!polder.isDryLand(x, z) || blocked(x, z)) continue;
        stampWillow(grid, P, polder, x, z, rng);
    }

    // --- fenced meadows, and sheep to put in them -------------------------
    for (let i = 0; i < 5; i++) {
        const x = 20 + Math.floor(rng() * (size - 50));
        const z = 20 + Math.floor(rng() * (size - 50));
        if (polder.distToCanal(x, z) < 18 || blocked(x, z)) continue;
        const w = 10 + Math.floor(rng() * 8);
        const d = 9 + Math.floor(rng() * 8);
        stampFence(grid, P, polder, x, z, x + w, z);
        stampFence(grid, P, polder, x + w, z, x + w, z + d);
        stampFence(grid, P, polder, x + w, z + d, x, z + d);
        stampFence(grid, P, polder, x, z + d, x, z);

        const flock = 2 + Math.floor(rng() * 4);
        for (let s = 0; s < flock; s++) {
            const sx = x + 2 + Math.floor(rng() * (w - 4));
            const sz = z + 2 + Math.floor(rng() * (d - 4));
            stampSheep(grid, P, polder, sx, sz, rng() < 0.5 ? 0 : 1, rng);
        }
    }

    scatterReeds(grid, P, polder, seed + 31);
    scatterTulips(grid, P, polder, seed + 57);

    return { lights };
}
